import { useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import StatsCard from '../../components/common/StatsCard';
import { Button } from '../../components/ui/button';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Filter,
  Download,
} from 'lucide-react';
import { TRADE_FILTER_FIELDS } from '../../constants/filters';
import { capitalizeFirst } from '../../utils';
import PageHeader from '../../components/common/PageHeader';

const mockTrades = [
  {
    id: 'TRD001',
    client: 'John Doe',
    symbol: 'EUR/USD',
    type: 'buy',
    volume: 0.1,
    openPrice: 1.085,
    closePrice: 1.0875,
    profit: 25.0,
    status: 'closed',
    openTime: '2024-01-15 10:30',
    closeTime: '2024-01-15 14:45',
  },
  {
    id: 'TRD002',
    client: 'Jane Smith',
    symbol: 'GBP/USD',
    type: 'sell',
    volume: 0.2,
    openPrice: 1.275,
    closePrice: 1.2725,
    profit: 50.0,
    status: 'closed',
    openTime: '2024-01-15 09:15',
    closeTime: '2024-01-15 11:30',
  },
  {
    id: 'TRD003',
    client: 'Bob Johnson',
    symbol: 'USD/JPY',
    type: 'buy',
    volume: 0.15,
    openPrice: 148.5,
    status: 'open',
    closePrice: '-',
    profit: 0,
    openTime: '2024-01-16 08:20',
    closeTime: '-',
  },
];

const TypeBadge = ({ type }: { type: string }) => (
  <span
    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
      type === 'buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}
  >
    {capitalizeFirst(type)}
  </span>
);

const StatusBadge = ({ status }: { status: string }) => {
  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'closed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusClasses(
        status
      )}`}
    >
      {capitalizeFirst(status)}
    </span>
  );
};

const ProfitDisplay = ({ profit }: { profit: number }) => (
  <span className={profit >= 0 ? 'text-green-600' : 'text-red-600'}>
    ${profit.toFixed(2)}
  </span>
);

const Trades = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const handleFiltersClear = useCallback(() => {
    setActiveFilters({});
  }, []);

  const handleFiltersApply = useCallback(
    (filters: { [key: string]: string }) => {
      setActiveFilters(filters);
    },
    []
  );

  const handleViewTrade = useCallback((tradeId: string) => {
    console.log('View trade:', tradeId);
  }, []);

  const handleEditTrade = useCallback((tradeId: string) => {
    console.log('Edit trade:', tradeId);
  }, []);

  const handleDeleteTrade = useCallback((tradeId: string) => {
    console.log('Delete trade:', tradeId);
  }, []);

  const handleCloseTrade = useCallback((tradeId: string) => {
    console.log('Close trade:', tradeId);
  }, []);

  const filteredTrades = useMemo(() => {
    let filtered = mockTrades;

    if (searchValue && searchValue.length >= 3) {
      filtered = filtered.filter(trade => {
        const searchTerm = searchValue.toLowerCase();
        return (
          trade.id.toLowerCase().includes(searchTerm) ||
          trade.client.toLowerCase().includes(searchTerm) ||
          trade.symbol.toLowerCase().includes(searchTerm) ||
          trade.type.toLowerCase().includes(searchTerm) ||
          trade.status.toLowerCase().includes(searchTerm) ||
          trade.openTime.toLowerCase().includes(searchTerm) ||
          trade.closeTime.toLowerCase().includes(searchTerm) ||
          trade.profit.toString().includes(searchTerm) ||
          trade.volume.toString().includes(searchTerm) ||
          trade.openPrice.toString().includes(searchTerm) ||
          trade.closePrice.toString().includes(searchTerm)
        );
      });
    }

    if (activeFilters && activeFilters.status && activeFilters.status !== '') {
      filtered = filtered.filter(
        trade => trade.status === activeFilters.status
      );
    }

    return filtered;
  }, [activeFilters, searchValue]);

  const columns = useMemo<DataTableColumn<(typeof mockTrades)[0]>[]>(
    () => [
      {
        id: 'id',
        header: 'Trade ID',
        accessor: row => <span className="font-medium">{row.id}</span>,
        sortable: true,
        sortValue: row => row.id,
        width: '120px',
      },
      {
        id: 'client',
        header: 'Client',
        accessor: row => row.client,
        sortable: true,
        sortValue: row => row.client,
        width: '160px',
      },
      {
        id: 'symbol',
        header: 'Symbol',
        accessor: row => row.symbol,
        sortable: true,
        sortValue: row => row.symbol,
        width: '150px',
      },
      {
        id: 'type',
        header: 'Type',
        accessor: row => <TypeBadge type={row.type} />,
        sortable: true,
        sortValue: row => row.type,
        width: '100px',
      },
      {
        id: 'volume',
        header: 'Volume',
        accessor: row => row.volume,
        sortable: true,
        sortValue: row => row.volume,
        width: '100px',
      },
      {
        id: 'openPrice',
        header: 'Open Price',
        accessor: row => row.openPrice,
        sortable: true,
        sortValue: row => row.openPrice,
        width: '140px',
      },
      {
        id: 'closePrice',
        header: 'Close Price',
        accessor: row => row.closePrice,
        sortable: true,
        sortValue: row => row.closePrice,
        width: '140px',
      },
      {
        id: 'profit',
        header: 'Profit',
        accessor: row => <ProfitDisplay profit={row.profit} />,
        sortable: true,
        sortValue: row => row.profit,
        width: '120px',
      },
      {
        id: 'status',
        header: 'Status',
        accessor: row => <StatusBadge status={row.status} />,
        sortable: true,
        sortValue: row => row.status,
        width: '120px',
      },
      {
        id: 'openTime',
        header: 'Open Time',
        accessor: row => row.openTime,
        sortable: true,
        sortValue: row => row.openTime,
        width: '180px',
      },
      {
        id: 'closeTime',
        header: 'Close Time',
        accessor: row => row.closeTime,
        sortable: true,
        sortValue: row => row.closeTime,
        width: '180px',
      },
      {
        id: 'actions',
        header: 'Actions',
        accessor: row => (
          <ActionDropdown
            id={row.id}
            onView={() => handleViewTrade(row.id)}
            onEdit={() => handleEditTrade(row.id)}
            onDelete={() => handleDeleteTrade(row.id)}
            additionalItems={
              row.status === 'open' && (
                <button
                  className="flex items-center gap-2 text-sm cursor-pointer w-full px-2 py-1.5 hover:bg-accent"
                  onClick={() => handleCloseTrade(row.id)}
                >
                  Close Trade
                </button>
              )
            }
          />
        ),
        width: '100px',
      },
    ],
    [handleViewTrade, handleEditTrade, handleDeleteTrade, handleCloseTrade]
  );

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Trade Management"
          description="Monitor and manage all trading activities"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Trades"
          value="1,204"
          change="+10% from last month"
          icon={Activity}
        />

        <StatsCard
          title="Open Trades"
          value="156"
          change="+5% from yesterday"
          icon={TrendingUp}
        />

        <StatsCard
          title="Total Volume"
          value="$45.2M"
          change="+8% from last month"
          icon={TrendingDown}
        />

        <StatsCard
          title="Total P&L"
          value="+$125K"
          change="+15% from last month"
          icon={Activity}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Trade List</CardTitle>
              <CardDescription>
                View and manage all client trades
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowInlineFilters(!showInlineFilters);
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={TRADE_FILTER_FIELDS}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Trades"
          />
          <DataTable
            columns={columns}
            data={filteredTrades}
            searchValue={searchValue}
            searchPlaceholder="Search Trade ID, Client, Symbol, Type, Volume, Open/Close Price, Status..."
            onSearchChange={setSearchValue}
            onSearchReset={() => setSearchValue('')}
            filterFields={TRADE_FILTER_FIELDS}
            activeFilters={activeFilters}
            onFiltersChange={setActiveFilters}
            initialPageSize={10}
            pageSizeOptions={[5, 10, 25, 50]}
            showColumnVisibility={true}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Trades;
